import React, { useState } from "react";
import { t } from "../i18n";
import { Copy, Check, CaretDown, Warning, SealWarning, Info, ShoppingBag, Cloud, ArrowsHorizontal, Equals } from "@phosphor-icons/react";
import ApplyToShopifyModal from "./ApplyToShopifyModal";
import ApplyAllToShopifyModal from "./ApplyAllToShopifyModal";
import WordDiff from "./WordDiff";

function SeverityBadge({ severity, lang }) {
  const map = {
    critical: { cls: "sev-critical", icon: <SealWarning size={12} weight="fill" /> },
    warning: { cls: "sev-warning", icon: <Warning size={12} weight="fill" /> },
    info: { cls: "sev-info", icon: <Info size={12} weight="fill" /> },
  };
  const { cls, icon } = map[severity] || map.info;
  return (
    <span
      className={`inline-flex items-center gap-1 border px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest ${cls}`}
      data-testid={`severity-badge-${severity}`}
    >
      {icon}
      {t(lang, `severity_${severity}`)}
    </span>
  );
}

function CopyButton({ text, lang }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      className="inline-flex items-center gap-1.5 border border-neutral-900 bg-white hover:bg-neutral-900 hover:text-white px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest ma-trans"
      data-testid="copy-suggested-button"
    >
      {copied ? <Check size={12} weight="bold" /> : <Copy size={12} />}
      {copied ? t(lang, "copied") : t(lang, "copy")}
    </button>
  );
}

function ViolationRow({ v, index, lang }) {
  const [open, setOpen] = useState(index < 2);
  const [applyOpen, setApplyOpen] = useState(false);
  const [diffMode, setDiffMode] = useState("inline"); // inline | side
  return (
    <div className="border-t border-neutral-200" data-testid={`violation-row-${index}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-neutral-50 ma-trans"
        data-testid={`violation-toggle-${index}`}
      >
        <SeverityBadge severity={v.severity} lang={lang} />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-neutral-900 truncate">
            <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mr-2">
              {v.category}
            </span>
            {v.rule}
          </div>
        </div>
        <CaretDown
          size={14}
          className={`text-neutral-500 ma-trans ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="px-4 pb-5 pt-1 space-y-3 ma-in">
          {/* Mode toggle */}
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setDiffMode("inline")}
              className={`inline-flex items-center gap-1.5 border px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest ma-trans ${
                diffMode === "inline"
                  ? "border-neutral-900 bg-neutral-900 text-white"
                  : "border-neutral-300 bg-white text-neutral-700 hover:border-neutral-900"
              }`}
              data-testid={`diff-mode-inline-${index}`}
              title={t(lang, "diff_inline_hint")}
            >
              <Equals size={12} weight="bold" />
              {t(lang, "diff_inline")}
            </button>
            <button
              type="button"
              onClick={() => setDiffMode("side")}
              className={`inline-flex items-center gap-1.5 border px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest ma-trans ${
                diffMode === "side"
                  ? "border-neutral-900 bg-neutral-900 text-white"
                  : "border-neutral-300 bg-white text-neutral-700 hover:border-neutral-900"
              }`}
              data-testid={`diff-mode-side-${index}`}
              title={t(lang, "diff_side_hint")}
            >
              <ArrowsHorizontal size={12} weight="bold" />
              {t(lang, "diff_side")}
            </button>
            <div className="ml-auto flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setApplyOpen(true)}
                className="inline-flex items-center gap-1.5 border border-emerald-700 bg-emerald-700 text-white hover:bg-emerald-800 px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest ma-trans"
                data-testid={`violation-apply-shopify-${index}`}
                title={t(lang, "apply_to_shopify_btn")}
              >
                <ShoppingBag size={12} weight="fill" />
                {t(lang, "apply_to_shopify_btn")}
              </button>
              <CopyButton text={v.suggested_text || ""} lang={lang} />
            </div>
          </div>

          {diffMode === "inline" ? (
            <div className="border border-neutral-300 bg-white" data-testid={`diff-inline-pane-${index}`}>
              <div className="flex items-center justify-between border-b border-neutral-300 px-3 py-1.5 bg-neutral-50">
                <span className="text-[10px] font-mono uppercase tracking-widest text-neutral-700">
                  {t(lang, "diff_inline_title")}
                </span>
                <span className="text-[10px] font-mono text-neutral-500">
                  <span className="line-through decoration-red-700/70 text-red-900 bg-red-100 px-1">{t(lang, "diff_removed")}</span>
                  <span className="mx-1">→</span>
                  <span className="bg-emerald-100 text-emerald-900 px-1">{t(lang, "diff_added")}</span>
                </span>
              </div>
              <div className="p-3">
                <WordDiff original={v.original_text || ""} suggested={v.suggested_text || ""} />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="border border-red-300 bg-red-50">
                <div className="flex items-center justify-between border-b border-red-300 px-3 py-1.5">
                  <span className="text-[10px] font-mono uppercase tracking-widest text-red-800">
                    {t(lang, "original")}
                  </span>
                </div>
                <pre
                  className="p-3 text-xs font-mono whitespace-pre-wrap text-red-900"
                  data-testid={`violation-original-${index}`}
                >
                  {v.original_text || "—"}
                </pre>
              </div>
              <div className="border border-emerald-400 bg-emerald-50">
                <div className="flex items-center justify-between gap-2 border-b border-emerald-400 px-3 py-1.5">
                  <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-800">
                    {t(lang, "suggested")}
                  </span>
                </div>
                <pre
                  className="p-3 text-xs font-mono whitespace-pre-wrap text-emerald-900"
                  data-testid={`violation-suggested-${index}`}
                >
                  {v.suggested_text || "—"}
                </pre>
              </div>
            </div>
          )}

          <div className="border border-neutral-200 bg-neutral-50 p-3">
            <span className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 block mb-1">
              Rationale
            </span>
            <p
              className="text-sm text-neutral-800 leading-relaxed"
              data-testid={`violation-explanation-${index}`}
            >
              {v.explanation}
            </p>
          </div>
        </div>
      )}

      <ApplyToShopifyModal
        open={applyOpen}
        onClose={() => setApplyOpen(false)}
        lang={lang}
        originalPhrase={v.original_text || ""}
        suggestedPhrase={v.suggested_text || ""}
      />
    </div>
  );
}

export default function ViolationsList({ violations, lang }) {
  const [applyAllOpen, setApplyAllOpen] = useState(false);

  if (!violations || violations.length === 0) {
    return (
      <div
        className="border border-emerald-600 bg-emerald-50 p-6 text-center"
        data-testid="no-violations"
      >
        <div className="text-emerald-800 font-display text-lg">
          {t(lang, "no_violations")}
        </div>
      </div>
    );
  }

  // Sort by severity critical > warning > info
  const order = { critical: 0, warning: 1, info: 2 };
  const sorted = [...violations].sort(
    (a, b) => (order[a.severity] ?? 9) - (order[b.severity] ?? 9)
  );

  return (
    <div className="border border-neutral-900 bg-white" data-testid="violations-list">
      <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-900 bg-neutral-900 text-white">
        <div className="text-xs uppercase tracking-[0.2em] font-mono">
          {t(lang, "violations")}
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <button
            type="button"
            onClick={() => setApplyAllOpen(true)}
            className="inline-flex items-center gap-1.5 border border-emerald-400 bg-emerald-600 text-white hover:bg-emerald-700 px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest ma-trans"
            data-testid="audit-apply-all-shopify"
          >
            <Cloud size={12} weight="fill" />
            {t(lang, "apply_all_btn")}
          </button>
          <span className="text-xs font-mono opacity-70">{sorted.length}</span>
        </div>
      </div>
      <div>
        {sorted.map((v, i) => (
          <ViolationRow key={v.id || i} v={v} index={i} lang={lang} />
        ))}
      </div>
      <ApplyAllToShopifyModal
        open={applyAllOpen}
        onClose={() => setApplyAllOpen(false)}
        lang={lang}
        rewrites={sorted.map((v) => ({
          original_phrase: v.original_text,
          suggested_phrase: v.suggested_text,
        }))}
      />
    </div>
  );
}
